@if (organisation$ | async; as organisation) {
  <div class="flex justify-center">
    <div class="w-full max-w-screen-lg px-4 py-6">
      <!-- Header -->
      <div class="flex flex-col md:flex-row md:items-center md:justify-between pb-4 border-b border-surface">
        <div class="flex flex-col gap-2">
          <a class="flex items-center gap-2" routerLink="/dashboard/settings">
            <span class="text-primary font-semibold">Settings</span>
            <i class="pi pi-chevron-right text-primary !text-xs"></i>
          </a>
          <div class="flex items-center gap-3">
            <i class="pi pi-shop !text-2xl"></i>
            <span class="text-3xl font-semibold">{{ organisationAlias }}</span>
          </div>
        </div>
      </div>

      <div class="flex gap-5 flex-col-reverse md:flex-row py-2">
        <div class="flex flex-col flex-auto gap-16">
          <form class="flex flex-col flex-auto gap-8" [formGroup]="organisationForm" (ngSubmit)="updateOrganisation(organisation)">
            <div class="flex flex-row items-center justify-between pb-2 border-b border-surface">
              <span class="text-xl font-semibold">Details</span>
              <div *appHasRole="UserRole.Owner; exact: true" class="flex flex-row justify-end gap-3">
                @if (!isEditMode()) {
                  <button
                    aria-label="Edit details"
                    class="w-auto"
                    icon="pi pi-pencil"
                    iconPos="left"
                    label="Edit"
                    pButton
                    pRipple
                    size="small"
                    type="button"
                    (click)="$event.preventDefault(); toggleOrganisationEdit()"></button>
                }
                @if (isEditMode()) {
                  <button
                    aria-label="Cancel editing"
                    class="p-button-outlined w-auto"
                    label="Cancel"
                    pButton
                    pRipple
                    size="small"
                    type="button"
                    (click)="$event.preventDefault(); toggleOrganisationEdit()"></button>
                }
                @if (isEditMode()) {
                  <button
                    aria-label="Save details"
                    class="w-auto"
                    label="Save"
                    pButton
                    pRipple
                    size="small"
                    type="submit"
                    [disabled]="organisationForm?.pristine || organisationForm.invalid"></button>
                }
              </div>
            </div>
            <div class="flex flex-row gap-4 items-center justify-between md:justify-start">
              <label class="font-semibold text-900 md:w-28" for="name">Name</label>
              <input formControlName="name" id="name" pInputText type="text" />
            </div>
            <div class="flex flex-row gap-4 items-center justify-between md:justify-start">
              <label class="font-semibold text-900 md:w-28" for="description">Description</label>
              <textarea formControlName="description" id="description" pTextarea rows="4"></textarea>
            </div>
            <div class="flex flex-row gap-4 items-center justify-between md:justify-start">
              <label class="font-semibold text-900 md:w-28" for="stripeCustomerId">Billing ID</label>
              <input
                id="stripeCustomerId"
                pInputText
                placeholder="{{ organisationAlias }} Customer ID"
                type="text"
                [disabled]="true"
                [value]="organisation.stripeCustomerId" />
            </div>
          </form>
        </div>
        <div class="w-full lg:w-1/3">
          <div class="flex flex-col items-center">
            <span class="font-semibold text-900 mb-2">Icon</span>
            <button
              class="rounded-full h-40 w-40 cursor-pointer border-0 bg-transparent p-0"
              tabindex="0"
              type="button"
              (click)="menu.toggle($event)"
              (keydown.enter)="menu.toggle($event)">
              <img
                alt="Organisation icon"
                class="rounded-full h-40 w-40"
                [height]="160"
                [ngSrc]="organisation.avatar ? organisation.avatar : 'logo/logo.svg'"
                [width]="160" />
            </button>
            <p-button icon="pi pi-upload" styleClass="-mt-4" [rounded]="true" (onClick)="menu.toggle($event)" />
            <p-fileUpload
              #fileUpload
              accept="image/*"
              mode="basic"
              name="test[]"
              styleClass="invisible"
              [auto]="true"
              [customUpload]="true"
              [maxFileSize]="1000000"
              (uploadHandler)="setImageFile($event, fileUpload, organisation)" />
          </div>
        </div>
      </div>

      <div class="flex flex-row items-center justify-between pb-2 border-b border-surface mt-16">
        <span class="text-xl font-semibold">Members</span>
      </div>

      <p-table
        dataKey="id"
        [currentPageReportTemplate]="'Showing {first} to {last} of {totalRecords} ' + index"
        [lazy]="true"
        [loading]="loading()"
        [paginator]="true"
        [rows]="rows"
        [rowsPerPageOptions]="[10, 20]"
        [showCurrentPageReport]="true"
        [totalRecords]="totalRecords()"
        [value]="users()"
        (onLazyLoad)="loadUsers($event)">
        <ng-template pTemplate="caption">
          <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p-iconfield iconPosition="left">
              <p-inputicon>
                <i class="pi pi-search"></i>
              </p-inputicon>
              <input
                class="w-full md:w-auto"
                inputStyleClass="w-full md:w-auto"
                pInputText
                pSize="small"
                placeholder="Filter by name or email..."
                styleClass="w-full md:w-auto"
                type="text"
                [ngModel]="searchQuery()"
                (ngModelChange)="onSearchQueryChange($event)" />
            </p-iconfield>
            <div class="flex gap-3 self-end">
              <button
                aria-label="Export CSV"
                class="p-button-secondary"
                icon="pi pi-download"
                label="Export CSV"
                pButton
                pRipple
                size="small"
                type="button"
                [loading]="exporting()"
                (click)="exportToCsv()"></button>
              <button
                appGateFeature="delegate-access"
                aria-label="Invite Member"
                icon="pi pi-user-plus"
                label="Invite Member"
                pButton
                pRipple
                size="small"
                type="button"
                (click)="invite(organisation)"></button>
            </div>
          </div>
        </ng-template>
        <ng-template pTemplate="header">
          <tr>
            <th>Member</th>
            <th>Last Login</th>
            <th class="w-48">Role</th>
            <th class="w-12"></th>
          </tr>
        </ng-template>
        <ng-template let-user pTemplate="body">
          <tr>
            <td>
              <div class="flex flex-col">
                <span class="font-semibold">{{ user.name }}</span>
                <span>{{ user.email }}</span>
              </div>
            </td>
            <td>{{ user.lastLoginTime ? (user.lastLoginTime | date: 'MMM d, yyyy, h:mm a') : 'Never' }}</td>
            <td>
              @if (organisation.ownerId === user.id) {
                <p-tag value="OWNER" />
              } @else {
                <p-select
                  #roleDropdown
                  appendTo="body"
                  styleClass="w-40"
                  [disabled]="updatingRoles.get(user.id)"
                  [loading]="updatingRoles.get(user.id)"
                  [ngModel]="organisation.roles[user.id]"
                  [options]="roleOptions"
                  (onChange)="updateUserRole(roleDropdown, organisation, user.id, $event, organisation.roles[user.id])" />
              }
            </td>
            <td>
              <div class="flex gap-2 justify-end">
                @if (organisation.ownerId !== user.id) {
                  <button
                    aria-label="Send Password Reset"
                    icon="pi pi-envelope"
                    pButton
                    pRipple
                    pTooltip="Send Password Reset"
                    tooltipPosition="top"
                    type="button"
                    [rounded]="true"
                    [text]="true"
                    (click)="sendPasswordReset(user)"></button>
                  <button
                    aria-label="Remove User"
                    icon="pi pi-user-minus"
                    pButton
                    pRipple
                    pTooltip="Remove User"
                    tooltipPosition="top"
                    type="button"
                    [rounded]="true"
                    [severity]="'danger'"
                    [text]="true"
                    (click)="confirmRemoveUser(user)"></button>
                }
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>

      <!-- API Keys -->
      <div *appHasRole="UserRole.Admin" class="flex flex-col flex-auto mt-16">
        <div class="flex flex-row items-center justify-between pb-2 border-b border-surface">
          <span class="text-xl font-semibold">API Keys</span>
          @if (organisation.hasApiAccess) {
            <p-button icon="pi pi-plus" label="Create API Key" size="small" (click)="apiKeys.openCreateDialog()" />
          }
        </div>
        <app-api-keys #apiKeys [hasApiAccess]="!!organisation.hasApiAccess" [organisationId]="organisation.id" />
      </div>

      <!-- Calendar Connections -->
      <div *appHasRole="UserRole.Admin" class="flex flex-col flex-auto mt-16">
        <div class="flex flex-row items-center justify-between pb-2 border-b border-surface">
          <span class="text-xl font-semibold">Calendar Connections</span>
          <p-button
            icon="pi pi-plus"
            label="Connect Google Account"
            size="small"
            [loading]="calendarConnections.isConnecting()"
            (click)="calendarConnections.connect()" />
        </div>
        <app-calendar-connections #calendarConnections [organisationId]="organisation.id" />
      </div>

      <!-- Danger Zone -->
      <div *appHasRole="UserRole.Owner" class="flex flex-col flex-auto gap-8 mt-16">
        <div class="flex flex-row items-center justify-between pb-2 border-b border-surface">
          <span class="text-xl font-semibold">Danger Zone</span>
        </div>

        <div *appHasRole="UserRole.Owner" class="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div class="flex flex-col">
            <span class="font-semibold text-900">Transfer Ownership</span>
            <span class="text-sm text-600"
              >Transfer ownership of this {{ organisationAlias.toLowerCase() }} to another member. You will lose admin privileges.</span
            >
          </div>
          <button
            aria-label="Transfer Ownership"
            class="min-w-fit self-end md:self-center"
            icon="pi pi-arrow-right-arrow-left"
            label="Transfer Ownership"
            pButton
            pRipple
            severity="danger"
            size="small"
            type="button"
            [outlined]="true"
            (click)="transferOwnership(organisation)"></button>
        </div>
      </div>
    </div>
  </div>
} @else {
  <div class="flex justify-center">
    <div class="w-full max-w-screen-lg px-4 py-6">
      <!-- Header Skeleton -->
      <div class="flex flex-col md:flex-row md:items-center md:justify-between pb-4 border-b border-surface">
        <div class="flex flex-col gap-2">
          <p-skeleton height="20px" width="100px" />
          <div class="flex items-center gap-3">
            <p-skeleton shape="circle" size="2rem" />
            <p-skeleton height="32px" width="120px" />
          </div>
        </div>
      </div>

      <div class="flex gap-5 flex-col-reverse md:flex-row py-2">
        <div class="flex flex-col flex-auto gap-16">
          <!-- Details Form Skeleton -->
          <div class="flex flex-col flex-auto gap-8">
            <div class="flex flex-row items-center justify-between pb-2 border-b border-surface">
              <p-skeleton height="24px" width="80px" />
              <div class="flex gap-3">
                <p-skeleton height="32px" width="80px" />
              </div>
            </div>
            <div class="flex flex-col gap-4">
              <div class="flex flex-row gap-4 items-center justify-between md:justify-start">
                <p-skeleton height="20px" width="100px" />
                <p-skeleton height="40px" width="200px" />
              </div>
              <div class="flex flex-row gap-4 items-center justify-between md:justify-start">
                <p-skeleton height="20px" width="100px" />
                <p-skeleton height="40px" width="200px" />
              </div>
              <div class="flex flex-row gap-4 items-center justify-between md:justify-start">
                <p-skeleton height="20px" width="100px" />
                <p-skeleton height="40px" width="200px" />
              </div>
            </div>
          </div>
        </div>
        <div class="w-full lg:w-1/3">
          <div class="flex flex-col items-center">
            <p-skeleton class="mb-2" height="20px" width="80px" />
            <p-skeleton shape="circle" size="10rem" />
            <p-skeleton class="-mt-4" shape="circle" size="2rem" />
          </div>
        </div>
      </div>

      <!-- Members Section Skeleton -->
      <div class="flex flex-row items-center justify-between pb-2 border-b border-surface mt-16">
        <p-skeleton height="24px" width="100px" />
      </div>
      <div class="flex flex-col gap-2 mt-2">
        <!-- Table Header -->
        <div class="flex justify-between items-center mb-4 border-b border-surface pb-2">
          <p-skeleton height="32px" width="300px" />
          <div class="flex gap-3">
            <p-skeleton height="32px" width="120px" />
            <p-skeleton height="32px" width="120px" />
          </div>
        </div>

        <!-- Table Headers -->
        <div class="flex items-center gap-4 border-b border-surface pb-2">
          <p-skeleton height="20px" width="40px" />
          <p-skeleton height="20px" width="200px" />
          <p-skeleton class="ml-auto" height="20px" width="150px" />
        </div>

        <!-- Table Rows -->
        <div class="flex flex-col gap-2">
          @for (i of [1, 2, 3]; track i) {
            <div class="flex items-center gap-4 py-2">
              <p-skeleton height="20px" width="40px" />
              <div class="flex-1">
                <p-skeleton height="20px" width="180px" />
              </div>
              <p-skeleton height="20px" width="120px" />
            </div>
          }
        </div>

        <!-- Pagination -->
        <div class="flex justify-between items-center mt-4 pt-4 border-t border-surface">
          <p-skeleton height="32px" width="200px" />
          <div class="flex gap-2">
            <p-skeleton height="32px" width="32px" />
            <p-skeleton height="32px" width="32px" />
            <p-skeleton height="32px" width="32px" />
          </div>
        </div>
      </div>
    </div>
  </div>
}
<p-menu #menu appendTo="body" [model]="avatarMenu" [popup]="true" />
